/**
 * RELOAD ON RELEASE: a tab whose bundle is no longer the deployed one reloads itself, at a
 * moment when the reload loses nothing.
 *
 * Why it must (#982): a release can change what a stored config MEANS, not only how it is
 * drawn. The signed-amount release relabels the amount columns and the server's v4 step
 * migrates old views at read time; a tab still running the previous bundle saves the views
 * it loaded earlier with the Reporting amount visible, stamped v4, and the server can never
 * migrate them again. `lazyWithRetry` only recovers a chunk that 404s, which a tab that has
 * already loaded every chunk it needs never hits.
 *
 * How it knows, without a dependency or a build step: the CRA build writes
 * `asset-manifest.json` beside `index.html`, naming the entry bundle by its content hash
 * (`files['main.js']`). The document loaded its own entry as a `<script>`, so a manifest
 * naming a script this document did not load means a newer build is deployed. In
 * `craco start` the entry is the unhashed `bundle.js` on both sides, so nothing ever differs.
 *
 * When it checks: every few minutes, and whenever the tab comes back into view or back
 * online — a tab left open across a deploy is the case this exists for.
 *
 * When it reloads: NOT as soon as it knows. A tab in use holds work a reload would drop —
 * an edit still in its ~600 ms write debounce, a save or an upload in flight, an upload
 * whose document is not yet attached. A stale tab reloads at the next safe moment instead:
 * after a route change or once the tab is hidden, when the settle window has let any
 * debounced write go out, nothing is in flight (`isBusy`) and the user has not started
 * anything new (a key or pointer press, or the tab coming back, gives the moment up until
 * the next one). Busy at that moment, it asks again a settle window later.
 *
 * Never in a loop: the build a reload was made for is remembered for the session, so a
 * document still stale after it (an `index.html` held by a cache) stays put rather than
 * reloading forever.
 */

/** The build a reload was made for — `sessionStorage`, so it outlives the reload only. */
export const BUILD_RELOAD_KEY = 'aivo-build-reload';

/** How often a tab asks, in ms. */
export const BUILD_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * How long a safe moment must stay quiet before the reload, in ms: longer than the app's
 * write debounces (600–800 ms), so an edit made just before the moment has gone out — and
 * shows in `isBusy` — by the time the reload is decided.
 */
export const BUILD_RELOAD_SETTLE_MS = 1500;

/** The entry bundle a manifest names, as a path (`/static/js/main.1a2b.js`), or `null`. */
export const deployedEntryOf = (manifest: unknown): string | null => {
  if (typeof manifest !== 'object' || manifest === null) return null;
  const files = (manifest as { files?: unknown }).files;
  if (typeof files !== 'object' || files === null) return null;
  const entry = (files as Record<string, unknown>)['main.js'];
  return typeof entry === 'string' && entry.length > 0 ? entry : null;
};

const pathOf = (src: string, base: string): string | null => {
  try {
    return new URL(src, base).pathname;
  } catch {
    return null;
  }
};

/**
 * Is the deployed entry one of the scripts this document loaded? Compared by PATH, so a
 * bundle served from another host (a `PUBLIC_URL` CDN) still matches its manifest entry.
 */
export const isLoadedEntry = (entry: string, scriptSrcs: readonly string[], base: string) => {
  const target = pathOf(entry, base);
  return target !== null && scriptSrcs.some((src) => pathOf(src, base) === target);
};

export interface BuildCheckDeps {
  /** The deployed manifest, parsed; rejects or resolves `null` when it cannot be read. */
  fetchManifest: () => Promise<unknown>;
  /** The `src` of every script the document loaded. */
  scriptSrcs: () => readonly string[];
  /** The document's URL, to resolve relative paths against. */
  base: string;
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
}

/**
 * What one check found: the tab runs the deployed build, runs an older one (`stale`, with
 * the deployed entry to reload onto), already reloaded for this one (`held`), or cannot tell.
 */
export type BuildCheck =
  | { outcome: 'current' | 'held' | 'unknown' }
  | { outcome: 'stale'; entry: string };

/**
 * One check; it never reloads. An unreadable manifest is `unknown`: an offline tab or a
 * deploy in progress is no reason to reload. A document still stale after the reload made
 * for a build is `held`, and a current one forgets the build it reloaded for.
 */
export async function checkDeployedBuild(deps: BuildCheckDeps): Promise<BuildCheck> {
  let manifest: unknown;
  try {
    manifest = await deps.fetchManifest();
  } catch {
    return { outcome: 'unknown' };
  }
  const entry = deployedEntryOf(manifest);
  if (entry === null) return { outcome: 'unknown' };
  if (isLoadedEntry(entry, deps.scriptSrcs(), deps.base)) {
    deps.storage.removeItem(BUILD_RELOAD_KEY);
    return { outcome: 'current' };
  }
  if (deps.storage.getItem(BUILD_RELOAD_KEY) === entry) return { outcome: 'held' };
  return { outcome: 'stale', entry };
}

export interface BuildWatchDeps extends BuildCheckDeps {
  reload: () => void;
  /** Is work in flight that a reload would drop — a save, an upload? */
  isBusy: () => boolean;
  /** Calls `listener` after every in-app route change (a new path); returns the unsubscribe. */
  onNavigate: (listener: () => void) => () => void;
}

/** The browser's half of the deps; the app adds `isBusy` and `onNavigate`. */
export const browserBuildDeps = (): Omit<BuildWatchDeps, 'isBusy' | 'onNavigate'> => ({
  fetchManifest: async () => {
    const response = await fetch(`${process.env.PUBLIC_URL ?? ''}/asset-manifest.json`, {
      cache: 'no-store',
    });
    return response.ok ? ((await response.json()) as unknown) : null;
  },
  scriptSrcs: () => Array.from(document.scripts, (script) => script.src).filter(Boolean),
  base: window.location.href,
  storage: window.sessionStorage,
  reload: () => window.location.reload(),
});

/**
 * Start checking; returns the function that stops. One check at a time: a slow manifest
 * read never stacks a second one behind it. A stale tab reloads at its next safe moment
 * (module comment), at most once per deployed build.
 */
export function watchDeployedBuild(
  deps: BuildWatchDeps,
  intervalMs: number = BUILD_CHECK_INTERVAL_MS,
  settleMs: number = BUILD_RELOAD_SETTLE_MS,
): () => void {
  let stopped = false;
  let inFlight = false;
  /** The deployed entry this tab is to reload onto at its next safe moment; `null` while none. */
  let owed: string | null = null;
  /** The pending reload attempt of the current safe moment. */
  let attempt: number | undefined;

  const giveUp = () => {
    window.clearTimeout(attempt);
    attempt = undefined;
  };
  const tryReload = () => {
    attempt = undefined;
    if (owed === null) return;
    if (deps.isBusy()) {
      attempt = window.setTimeout(tryReload, settleMs);
      return;
    }
    deps.storage.setItem(BUILD_RELOAD_KEY, owed);
    owed = null;
    deps.reload();
  };
  /** A route change, or the tab hidden: reload once the moment has stayed quiet. */
  const safeMoment = () => {
    if (owed === null || stopped) return;
    giveUp();
    attempt = window.setTimeout(tryReload, settleMs);
  };

  const check = () => {
    if (inFlight) return;
    inFlight = true;
    void checkDeployedBuild(deps)
      .then((found) => {
        if (found.outcome === 'current') {
          owed = null;
          giveUp();
        } else if (found.outcome === 'stale') {
          owed = found.entry;
          // A tab found stale while hidden is already at its safe moment.
          if (document.visibilityState === 'hidden' && attempt === undefined) safeMoment();
        }
      })
      .finally(() => {
        inFlight = false;
      });
  };
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      safeMoment();
    } else {
      giveUp();
      check();
    }
  };
  /** The user started something new: this moment is no longer safe. */
  const onUserActs = () => giveUp();

  const timer = window.setInterval(check, intervalMs);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('online', check);
  window.addEventListener('keydown', onUserActs, true);
  window.addEventListener('pointerdown', onUserActs, true);
  const stopNavigation = deps.onNavigate(safeMoment);
  return () => {
    stopped = true;
    giveUp();
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('online', check);
    window.removeEventListener('keydown', onUserActs, true);
    window.removeEventListener('pointerdown', onUserActs, true);
    stopNavigation();
  };
}
